import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { medicationFromDb, medicationToDb } from '@/lib/mappers'

export function useMedications(activeTermId, userId) {
  const [medications, setMedications] = useState([])
  const [medLog, setMedLog] = useState({})

  async function loadMedications(termId) {
    const [medsRes, medLogRes] = await Promise.all([
      supabase.from('medications').select('*').eq('term_id', termId),
      supabase.from('med_logs').select('log').eq('term_id', termId).single(),
    ])
    const mapped = (medsRes.data || []).map(medicationFromDb)
    setMedications(mapped)
    setMedLog(medLogRes.data?.log || {})
    return { medications: mapped, medLog: medLogRes.data?.log || {} }
  }

  async function addMedication(med) {
    setMedications(prev => [...prev, med])
    if (activeTermId) {
      const { error } = await supabase.from('medications').insert(medicationToDb(med, activeTermId, userId))
      if (error) {
        // Roll back the optimistic add so the UI doesn't show an unsaved med.
        console.error('Failed to add medication:', error)
        setMedications(prev => prev.filter(m => m.id !== med.id))
        return { error }
      }
    }
    return { error: null }
  }

  async function deleteMedication(medId) {
    setMedications(prev => prev.filter(m => m.id !== medId))
    setMedLog(prev => {
      const next = {}
      for (const [date, entries] of Object.entries(prev)) {
        const { [medId]: _, ...rest } = entries
        if (Object.keys(rest).length > 0) next[date] = rest
      }
      return next
    })
    if (activeTermId) {
      await supabase.from('medications').delete().eq('id', medId).eq('term_id', activeTermId)
      const { data } = await supabase.from('med_logs').select('log').eq('term_id', activeTermId).single()
      if (data?.log) {
        const log = { ...data.log }
        for (const date of Object.keys(log)) {
          if (log[date][medId] !== undefined) {
            const { [medId]: _, ...rest } = log[date]
            if (Object.keys(rest).length > 0) {
              log[date] = rest
            } else {
              delete log[date]
            }
          }
        }
        await supabase.from('med_logs').upsert({ term_id: activeTermId, user_id: userId, log })
      }
    }
  }

  async function updateMedication(medId, updates) {
    const previous = medications.find(m => m.id === medId)
    setMedications(prev => prev.map(m =>
      m.id === medId ? { ...m, ...updates } : m
    ))
    if (activeTermId) {
      const dbUpdates = {}
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.timeOfDay !== undefined) dbUpdates.time_of_day = updates.timeOfDay
      if (updates.reminderTime !== undefined) dbUpdates.reminder_time = updates.reminderTime || null
      if (updates.frequency !== undefined) dbUpdates.frequency = updates.frequency
      const { error } = await supabase.from('medications').update(dbUpdates).eq('id', medId).eq('term_id', activeTermId)
      if (error) {
        // Restore the previous values so the UI reflects what's actually saved.
        console.error('Failed to update medication:', error)
        if (previous) setMedications(prev => prev.map(m => m.id === medId ? previous : m))
        return { error }
      }
    }
    return { error: null }
  }

  async function toggleMedLog(dateStr, medId) {
    const newLog = {
      ...medLog,
      [dateStr]: {
        ...medLog[dateStr],
        [medId]: !medLog[dateStr]?.[medId],
      },
    }
    setMedLog(newLog)
    if (activeTermId) {
      await supabase.from('med_logs').upsert({
        term_id: activeTermId,
        user_id: userId,
        log: newLog,
      })
    }
  }

  function clearMedications() {
    setMedications([])
    setMedLog({})
  }

  return {
    medications,
    medLog,
    _setMedicationsRaw: setMedications,
    _setMedLogRaw: setMedLog,
    loadMedications,
    addMedication,
    deleteMedication,
    updateMedication,
    toggleMedLog,
    clearMedications,
  }
}
